import { Canceler } from 'axios';
import {
  BucketServiceFileBreadcrumbDto,
  BucketServiceFileBreadcrumbDtoTypeEnum,
  BucketServiceFileItemDto,
  BucketServiceFileItemTypeDto,
} from '@nimbus/bucket-service/lib/api';
import { Permissions } from './permissions';

export interface GetBucketFilesParams {
  resetCache?: boolean;
  portalId?: string;
  workspaceId?: string;
  orgId?: string;
  limitSize?: number;
  limitFrom?: number;
  sortField?: string;
  sortOrder?: string;
}

export interface TempFileDto {
  name: string;
  type: string;
  filename: string;
  extra: unknown;
  kind: string;
  hash: string;
}

export interface BucketFile {
  globalId: string;
  bucketId: string;
  portalId?: string;
  target: string;
  targetId: string;
  orgId?: string;
  workspaceId: string;
  commentThreadId?: string;
  filename: string;
  type: FileType;
  format?: string;
  userId: number;
  size: number;
  createdAt: number;
  deleted: boolean;
  url: string;
  extra?: object;
  breadcrumbs?: FileBreadcrumb[];
  isNew?: boolean;
  permissions?: Permissions;
  pageId?: string;
}

export enum FileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOC = 'doc',
  ARCHIVE = 'archive',
  FILE = 'file',
}

export interface FileBreadcrumb {
  type: FileBreadcrumbType;
  name?: string;
  url?: string;
  prefixLocKey?: string;
}

export enum FileBreadcrumbType {
  PORTAL = 'portal',
  PORTAL_PAGE = 'portalPage',
  PORTAL_GLOBAL = 'portalGlobal',
  PORTAL_FILES = 'portalFiles',
  NOTE = 'note',
}

export enum UploadFileStatus {
  IN_PROGRESS = 'in-progress',
  CANCELED = 'canceled',
  ERROR = 'error',
  DONE = 'done',
}
export interface UploadFile {
  title: string;
  size: number;
  id: string;
  target: string;
  targetId: string;
  status: UploadFileStatus;
  uploadedSize: number;
  createdAt: number;
  canceler?: Canceler;
}

export function mapBucketFilesDtoToBucketFiles(files: BucketServiceFileItemDto[]): BucketFile[] {
  return files.map(mapBucketFileDtoToBucketFile);
}
export function mapBucketFileDtoToBucketFile(file: BucketServiceFileItemDto): BucketFile {
  return {
    globalId: file.globalId,
    bucketId: file.bucketId,
    portalId: file.portalId,
    target: file.target,
    targetId: file.targetId,
    orgId: file.orgId,
    workspaceId: file.workspaceId,
    commentThreadId: file.commentThreadId,
    filename: file.filename,
    type: mapBucketFileTypeDtoToBucketFileType(file.type),
    format: file.format,
    userId: file.userId,
    size: file.size,
    createdAt: file.createdAt * 1000,
    deleted: file.deleted,
    url: String(file.url),
    extra: file.extra,
    breadcrumbs: file.breadcrumbs ? mapBucketFileBreadcrumbsDtoToBucketFileBreadcrumbs(file.breadcrumbs) : undefined,
    permissions: file.permissions,
    pageId: 'pageId' in file ? (file.pageId as string) : undefined,
  };
}

export function mapBucketFileTypeDtoToBucketFileType(type: BucketServiceFileItemTypeDto): FileType {
  switch (type) {
    case 'file':
      return FileType.FILE;
    case 'archive':
      return FileType.ARCHIVE;
    case 'image':
      return FileType.IMAGE;
    case 'video':
      return FileType.VIDEO;
    case 'audio':
      return FileType.AUDIO;
    case 'doc':
      return FileType.DOC;
  }
}

export function mapBucketFileBreadcrumbsDtoToBucketFileBreadcrumbs(
  breadcrumbs: BucketServiceFileBreadcrumbDto[]
): FileBreadcrumb[] {
  return breadcrumbs.map(mapBucketFileBreadcrumbDtoToBucketFileBreadcrumb);
}

export function mapBucketFileBreadcrumbDtoToBucketFileBreadcrumb(
  breadcrumb: BucketServiceFileBreadcrumbDto
): FileBreadcrumb {
  return {
    type: mapBucketFileBreadcrumbTypeDtoToBucketFileBreadcrumbType(breadcrumb.type),
    name: breadcrumb.name,
    url: breadcrumb.url,
  };
}

export function mapBucketFileBreadcrumbTypeDtoToBucketFileBreadcrumbType(
  type: BucketServiceFileBreadcrumbDtoTypeEnum
): FileBreadcrumbType {
  switch (type) {
    case 'portal':
      return FileBreadcrumbType.PORTAL;
    case 'portalPage':
      return FileBreadcrumbType.PORTAL_PAGE;
    case 'portalGlobal':
      return FileBreadcrumbType.PORTAL_GLOBAL;
    case 'portalFiles':
      return FileBreadcrumbType.PORTAL_FILES;
    case 'note':
      return FileBreadcrumbType.NOTE;
  }
}

export interface GroupInclusion {
  id: number;
  target: string;
  targetId: string;
  bucketPk: number;
  bucketId: string;
}
